import type { SvgIconProps } from '@mui/material';
import type { ComponentType } from 'react';
import { useMemo } from 'react';
import {
  selectIsOrganizationDataAccessible,
  selectViewsRestrictions,
  ViewAccessType,
  ViewType,
} from '@/entities/session';
import { ROUTES } from '@/shared/constants';
import { useAppSelector } from '@/shared/model';
import {
  CalendarChartIcon,
  CalendarInsightsIcon,
  CohortChartIcon,
  CohortHistoryChartIcon,
  CohortMarketingPerformanceIcon,
  CohortRetentionIcon,
  CohortScannerIcon,
  Features,
  ROIBreakdownIcon,
  useFeatureFlags,
} from '@/shared/ui';

export enum ExploreSection {
  cohort = 'Cohort',
  calendar = 'Calendar',
}

export interface ExploreView {
  route: string;
  viewType: ViewType;
  section: ExploreSection;
  label: string;
  Icon: ComponentType<SvgIconProps>;
  /** When set, the view is additionally gated behind a client-side flag. */
  feature?: Features;
}

export const EXPLORE_VIEWS: ExploreView[] = [
  {
    route: ROUTES.cohortMarketingPerformance,
    viewType: ViewType.cohortMarketingPerformance,
    section: ExploreSection.cohort,
    label: 'Cohort Marketing Performance',
    Icon: CohortMarketingPerformanceIcon,
  },
  {
    route: ROUTES.cohortChart,
    viewType: ViewType.cohortChart,
    section: ExploreSection.cohort,
    label: 'Cohort Chart',
    Icon: CohortChartIcon,
  },
  {
    route: ROUTES.cohortHistoryChart,
    viewType: ViewType.cohortHistory,
    section: ExploreSection.cohort,
    label: 'Cohort History',
    Icon: CohortHistoryChartIcon,
    feature: Features.cohortHistory,
  },
  {
    route: ROUTES.cohortRetention,
    viewType: ViewType.cohortRetention,
    section: ExploreSection.cohort,
    label: 'Cohort Retention',
    Icon: CohortRetentionIcon,
  },
  {
    route: ROUTES.cohortScanner,
    viewType: ViewType.cohortScanner,
    section: ExploreSection.cohort,
    label: 'Cohort Scanner',
    Icon: CohortScannerIcon,
  },
  {
    route: ROUTES.calendarInsights,
    viewType: ViewType.calendarInsights,
    section: ExploreSection.calendar,
    label: 'Calendar Insights',
    Icon: CalendarInsightsIcon,
  },
  {
    route: ROUTES.calendarChart,
    viewType: ViewType.calendarChart,
    section: ExploreSection.calendar,
    label: 'Calendar Chart',
    Icon: CalendarChartIcon,
  },
  {
    route: ROUTES.roiBreakdown,
    viewType: ViewType.roiBreakdown,
    section: ExploreSection.calendar,
    label: 'ROI Breakdown',
    Icon: ROIBreakdownIcon,
  },
];

export const EXPLORE_ROUTES = EXPLORE_VIEWS.map(({ route }) => route);

/**
 * Views the current user may open, in priority order.
 */
export const useAllowedExploreViews = (): ExploreView[] => {
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const isOrganizationDataAccessible = useAppSelector(
    selectIsOrganizationDataAccessible,
  );
  const { isFeatureAvailable } = useFeatureFlags();

  return useMemo(() => {
    if (!isOrganizationDataAccessible) {
      return [];
    }

    return EXPLORE_VIEWS.filter(({ viewType, feature }) => {
      if (viewsRestrictions?.[viewType] === ViewAccessType.DENIED) {
        return false;
      }

      return feature ? isFeatureAvailable(feature) : true;
    });
  }, [viewsRestrictions, isOrganizationDataAccessible, isFeatureAvailable]);
};

/**
 * Whether any Explore view is permitted, ignoring organization data
 * accessibility. This deliberately differs from `useAllowedExploreViews`: the
 * saved-presets section stays reachable while an organization's data is still
 * pending.
 */
export const useIsAnyExploreViewPermitted = (): boolean => {
  const viewsRestrictions = useAppSelector(selectViewsRestrictions);
  const { isFeatureAvailable } = useFeatureFlags();

  return useMemo(() => {
    return EXPLORE_VIEWS.some(({ viewType, feature }) => {
      if (viewsRestrictions?.[viewType] === ViewAccessType.DENIED) {
        return false;
      }

      return feature ? isFeatureAvailable(feature) : true;
    });
  }, [viewsRestrictions, isFeatureAvailable]);
};
